import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export enum ImpersonationMode {
  INACTIVE = 'INACTIVE',
  STARTING = 'STARTING',
  ACTIVE = 'ACTIVE',
  ENDING = 'ENDING',
}

export interface ImpersonationRealUser {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

interface ImpersonationState {
  mode: ImpersonationMode;
  sessionId?: number;
  realUser?: ImpersonationRealUser;
  error?: string;
}

const initialState: ImpersonationState = {
  mode: ImpersonationMode.INACTIVE,
};

/**
 * Hydration payload as it arrives from `/api/portal/v1/context/`
 * (snake_case from Django).
 */
interface HydratePayload {
  session_id: number;
  real_user: {
    id: number;
    email: string;
    first_name: string | null;
    last_name: string | null;
  };
}

const impersonationSlice = createSlice({
  name: 'impersonation',
  initialState,
  reducers: {
    /**
     * Apply state from /context/ bootstrap. Called whether or not impersonation
     * is active — pass `null` to clear.
     */
    hydrate(state, action: PayloadAction<HydratePayload | null | undefined>) {
      const payload = action.payload;
      if (!payload) {
        return initialState;
      }
      state.mode = ImpersonationMode.ACTIVE;
      state.sessionId = payload.session_id;
      state.realUser = {
        id: payload.real_user.id,
        email: payload.real_user.email,
        firstName: payload.real_user.first_name,
        lastName: payload.real_user.last_name,
      };
      state.error = undefined;
    },
    setStarting(state) {
      state.mode = ImpersonationMode.STARTING;
      state.error = undefined;
    },
    setActive(state, action: PayloadAction<HydratePayload>) {
      state.mode = ImpersonationMode.ACTIVE;
      state.sessionId = action.payload.session_id;
      state.realUser = {
        id: action.payload.real_user.id,
        email: action.payload.real_user.email,
        firstName: action.payload.real_user.first_name,
        lastName: action.payload.real_user.last_name,
      };
      state.error = undefined;
    },
    setEnding(state) {
      state.mode = ImpersonationMode.ENDING;
      state.error = undefined;
    },
    clear() {
      return initialState;
    },
    setError(state, action: PayloadAction<string>) {
      state.error = action.payload;
    },
  },
  selectors: {
    getMode: state => state.mode,
    getSessionId: state => state.sessionId,
    getRealUser: state => state.realUser,
    isImpersonating: state =>
      state.mode === ImpersonationMode.ACTIVE
      || state.mode === ImpersonationMode.STARTING
      || state.mode === ImpersonationMode.ENDING,
    getError: state => state.error,
  },
});

export const {
  hydrate: hydrateImpersonation,
  setStarting: setImpersonationStarting,
  setActive: setImpersonationActive,
  setEnding: setImpersonationEnding,
  clear: clearImpersonation,
  setError: setImpersonationError,
} = impersonationSlice.actions;

export const {
  getMode: getImpersonationMode,
  getSessionId: getImpersonationSessionId,
  getRealUser: getImpersonationRealUser,
  isImpersonating,
  getError: getImpersonationError,
} = impersonationSlice.selectors;

export default impersonationSlice;
